/**
 * Office wall display — publicly accessible via /wall/:slug.
 * Auto-rotates through 5 slides (Sales Targets · Leads · Top Deals · Projects · Support)
 * every 30 s and refreshes data every 60 s. Dark theme, big type, no chrome.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Target, Users, Briefcase, HardHat, LifeBuoy, AlertTriangle, TrendingUp, Megaphone, ChevronRight, Radio, Mail, PartyPopper, PhoneCall, Store } from "lucide-react";
import { XentraWordmark } from "@/components/XentraLogo";

const SLIDES = ["sales", "leads", "deals", "projects", "support", "marketing"];
const ROTATE_MS = 30_000;
const PAUSE_AFTER_MANUAL_MS = 60_000; // pause auto-rotate for 60s after a manual click
const REFRESH_MS = 60_000;
const BASE = process.env.REACT_APP_BACKEND_URL;

const MARKETING_TYPE_META = {
  webinar:        { label: "Webinar",        Icon: Radio,       color: "text-blue-300 bg-blue-500/10 border-blue-500/30" },
  email_campaign: { label: "Email Campaign", Icon: Mail,        color: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
  event:          { label: "Event",          Icon: PartyPopper, color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  call_out_day:   { label: "Call-Out Day",   Icon: PhoneCall,   color: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  tradeshow:      { label: "Tradeshow",      Icon: Store,       color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

const gbp = (n) => "£" + Math.round(Number(n) || 0).toLocaleString("en-GB");
const pct = (n) => (Number(n) || 0).toFixed(0) + "%";

export default function WallDisplay() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [authState, setAuthState] = useState("checking");   // checking | signed_out | forbidden | ok
  const [slideIdx, setSlideIdx] = useState(0);
  const [now, setNow] = useState(new Date());
  const [pausedUntil, setPausedUntil] = useState(0);

  const startSignIn = async () => {
    try {
      sessionStorage.setItem("post_login_redirect", `/wall/${slug}`);
    } catch { /* ignore */ }
    try {
      const r = await fetch(`${BASE}/api/auth/microsoft/login?flow=staff&origin=${encodeURIComponent(window.location.origin)}`, { credentials: "include" });
      const j = await r.json();
      if (j.authorization_url) window.location.href = j.authorization_url;
      else setError("Microsoft sign-in is not configured");
    } catch (e) { setError(String(e.message || e)); }
  };

  const signOut = async () => {
    try { await fetch(`${BASE}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* ignore */ }
    setAuthState("signed_out");
    setData(null);
  };

  const load = async () => {
    try {
      const r = await fetch(`${BASE}/api/wall/${encodeURIComponent(slug)}/data`, { cache: "no-store", credentials: "include" });
      if (r.status === 401) {
        setAuthState("signed_out"); setData(null); setError(null);
        return;
      }
      if (r.status === 403) {
        setAuthState("forbidden"); setData(null); setError(null);
        return;
      }
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      setData(await r.json()); setError(null); setAuthState("ok");
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  useEffect(() => {
    load();
    const dataTimer = setInterval(load, REFRESH_MS);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(dataTimer); clearInterval(clockTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const t = setInterval(() => {
      // Skip auto-advance while user has recently clicked "next"
      if (Date.now() < pausedUntil) return;
      setSlideIdx((i) => (i + 1) % SLIDES.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pausedUntil]);

  const advance = () => {
    setSlideIdx((i) => (i + 1) % SLIDES.length);
    setPausedUntil(Date.now() + PAUSE_AFTER_MANUAL_MS);
  };

  // Keyboard: press → or space to jump to the next slide
  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight" || e.key === " ") advance(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user lands on the wall URL without a session, redirect them
  // straight to Microsoft SSO after ~1.5s (the button is still there as a
  // fallback in case the redirect is blocked).
  useEffect(() => {
    if (authState !== "signed_out") return;
    const t = setTimeout(() => { startSignIn(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);


  if (authState === "signed_out") return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-12" data-testid="wall-signin">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center gap-3 mb-6 px-4 py-1.5 rounded-full border border-white/10 text-slate-400 uppercase tracking-[0.3em] text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 pulse-dot" /> Wall Display
        </div>
        <h1 className="text-5xl font-black tracking-tight mb-4">Xentra Pulse</h1>
        <p className="text-slate-400 mb-8">Sign in with your Microsoft account to view the office wall display.</p>
        <button
          onClick={startSignIn}
          data-testid="wall-signin-btn"
          className="inline-flex items-center gap-3 rounded-md bg-white px-6 py-3 text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
        >
          <svg viewBox="0 0 21 21" className="h-5 w-5"><rect x="0" y="0" width="10" height="10" fill="#F25022"/><rect x="11" y="0" width="10" height="10" fill="#7FBA00"/><rect x="0" y="11" width="10" height="10" fill="#00A4EF"/><rect x="11" y="11" width="10" height="10" fill="#FFB900"/></svg>
          Sign in with Microsoft
        </button>
        <p className="text-slate-600 text-xs mt-6">Redirecting automatically…</p>
        {error && <p className="text-rose-400 text-xs mt-3">{error}</p>}
      </div>
    </div>
  );

  if (authState === "forbidden") return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-12" data-testid="wall-forbidden">
      <div className="text-center max-w-lg">
        <AlertTriangle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
        <div className="text-3xl font-bold mb-3">Not authorised for this wall display</div>
        <div className="text-slate-400 text-sm mb-6">Only Charles Ramsay, Henry Doyle and Ben Carr can open the wall display. Sign out and try again with a permitted account.</div>
        <button onClick={signOut} data-testid="wall-signout-btn" className="rounded-md border border-white/20 px-5 py-2 text-sm hover:bg-white/5">
          Sign out
        </button>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-12" data-testid="wall-error">
      <div className="text-center max-w-lg">
        <AlertTriangle className="h-16 w-16 text-rose-400 mx-auto mb-4" />
        <div className="text-3xl font-bold mb-3">Wall display unavailable</div>
        <div className="text-slate-400 text-sm mb-2">{error}</div>
        <div className="text-slate-500 text-xs">Ask an admin to check Support → Wall Display configuration.</div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center text-2xl font-mono" data-testid="wall-loading">
      Loading…
    </div>
  );

  const view = SLIDES[slideIdx];
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col overflow-hidden" data-testid={`wall-slide-${view}`}>
      {/* Header — clock & period on the left, Xentra brand mark on the right. */}
      <header className="px-10 pt-8 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">Period</div>
            <div className="text-lg font-semibold text-slate-200">{data.period_label}</div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">Now</div>
            <div className="text-lg font-semibold text-slate-200 tabular-nums">
              {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>
        <XentraWordmark variant="white" size={12} className="shrink-0" />
      </header>

      {/* Slide */}
      <main className="flex-1 px-10 pb-4 flex flex-col">
        {view === "sales" && <SalesTargets rows={data.sales_targets} teams={data.sales_targets_teams} business={data.sales_targets_business} periodLabel={data.sales_targets_period_label} mrrSummary={data.mrr_summary} />}
        {view === "leads" && <LeadsView leads={data.leads} summary={data.leads_summary} />}
        {view === "deals" && <TopDeals deals={data.top_deals} />}
        {view === "projects" && <ProjectsView projects={data.projects} />}
        {view === "support" && <SupportView support={data.support} />}
        {view === "marketing" && <MarketingView activities={data.marketing_activities} periodLabel={data.marketing_activities_period_label} />}
      </main>

      {/* Manual next-slide arrow — bottom-right, always visible */}
      <button
        onClick={advance}
        aria-label="Next slide"
        title="Next slide (or press → / Space)"
        className="fixed bottom-8 right-8 z-50 h-16 w-16 rounded-full bg-emerald-500/20 hover:bg-emerald-500/40 border-2 border-emerald-400/40 hover:border-emerald-400 backdrop-blur-sm text-emerald-300 hover:text-emerald-200 flex items-center justify-center transition-all hover:scale-110 active:scale-95 shadow-lg shadow-emerald-500/20"
        data-testid="wall-next-slide"
      >
        <ChevronRight className="h-8 w-8" strokeWidth={2.5} />
      </button>

      {/* Footer progress */}
      <footer className="px-10 pb-4">
        <div className="flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-all duration-500 ${i === slideIdx ? "bg-emerald-400" : "bg-slate-800"}`}
              data-testid={`wall-slide-progress-${s}`}
            />
          ))}
        </div>
        <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600 flex justify-between">
          <span>{view}</span>
          <span>Auto-refresh · {new Date(data.generated_at).toLocaleTimeString("en-GB")}</span>
        </div>
      </footer>
    </div>
  );
}


function SlideHeader({ icon: Icon, label, title }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 text-blue-400">
        <Icon className="h-5 w-5" />
        <div className="text-xs font-mono uppercase tracking-[0.3em]">{label}</div>
      </div>
      <div className="mt-1 text-5xl font-black tracking-tight text-white">{title}</div>
    </div>
  );
}


function SalesTargets({ rows, teams, business, periodLabel, mrrSummary }) {
  const label = periodLabel || "this quarter";
  const nothingResolved = (!rows || rows.length === 0) && (!teams || teams.length === 0) && !business;
  const colourFor = (pct) =>
    pct >= 100 ? "bg-emerald-400" : pct >= 60 ? "bg-blue-400" : pct >= 30 ? "bg-amber-400" : "bg-rose-400";
  const textFor = (pct) => (pct >= 100 ? "text-emerald-400" : "text-slate-200");

  // Dynamic card sizing so ALL targets fit — density scales with count.
  const density = (n) => (n <= 3 ? "p-5 text-lg" : n <= 6 ? "p-4 text-base" : "p-3 text-sm");

  const Card = ({ row, tone = "individual", compact = "" }) => {
    const bar = Math.min(100, Math.max(0, row.pct));
    const bgTone =
      tone === "business" ? "bg-gradient-to-br from-slate-900 to-slate-800 border-blue-800"
      : tone === "team" ? "bg-slate-900 border-violet-800/60"
      : "bg-slate-900 border-slate-800";
    const pctSize = compact.includes("text-sm") ? "text-2xl" : compact.includes("text-base") ? "text-3xl" : "text-4xl";
    return (
      <div className={`${bgTone} border rounded-2xl ${compact || "p-5"}`}>
        <div className="flex items-baseline justify-between mb-2">
          <div className="font-bold text-white truncate pr-3">{row.owner}</div>
          <div className={`${pctSize} font-black tabular-nums ${textFor(row.pct)}`}>{pct(row.pct)}</div>
        </div>
        <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden mb-2">
          <div className={`h-full ${colourFor(row.pct)} rounded-full transition-all`} style={{ width: `${bar}%` }} />
        </div>
        <div className="text-[11px] font-mono text-slate-500">{gbp(row.actual)} / {gbp(row.target)} MRR</div>
      </div>
    );
  };

  const SectionLabel = ({ children, count }) => (
    <div className="flex items-baseline gap-2 mb-2">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-blue-400">{children}</div>
      <div className="text-[10px] font-mono text-slate-600">·  {count}</div>
    </div>
  );

  // Individual grid columns: scale up as more targets exist so all fit on-screen
  const rowCount = rows?.length || 0;
  const indGrid = rowCount <= 3 ? "grid-cols-3"
    : rowCount <= 6 ? "grid-cols-3"
    : rowCount <= 8 ? "grid-cols-4"
    : "grid-cols-5";
  const indDensity = density(rowCount);
  const teamGrid = (teams?.length || 0) <= 1 ? "grid-cols-1"
    : (teams?.length || 0) <= 2 ? "grid-cols-2"
    : (teams?.length || 0) <= 4 ? "grid-cols-2"
    : "grid-cols-3";
  const teamDensity = density(teams?.length || 0);

  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={Target} label="Commercial" title={`Sales targets — ${label}`} />
      {/* Headline MRR actual-vs-target — ALWAYS rendered, even at 0%, so a
          missing business target doesn't leave the slide blank. */}
      {mrrSummary && (() => {
        const pct = mrrSummary.pct || 0;
        const bar = Math.min(100, Math.max(0, pct));
        return (
          <div
            className="mb-4 rounded-2xl border border-blue-800/60 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 p-5"
            data-testid="wall-mrr-summary"
          >
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-blue-400">
                Business MRR {mrrSummary.has_target ? "" : "· no target set yet"}
              </div>
              <div className={`text-5xl font-black tabular-nums ${textFor(pct)}`}>{pct}%</div>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden my-3">
              <div className={`h-full ${colourFor(pct)} rounded-full transition-all`} style={{ width: `${bar}%` }} />
            </div>
            <div className="text-sm font-mono text-slate-300 flex items-baseline justify-between">
              <span className="text-white"><span className="text-slate-500">Actual</span> {gbp(mrrSummary.actual)}</span>
              <span className="text-slate-400"><span className="text-slate-500">Target</span> {mrrSummary.has_target ? gbp(mrrSummary.target) : "—"}</span>
            </div>
          </div>
        );
      })()}
      {nothingResolved ? (
        <div className="flex-1 flex items-center justify-center text-slate-500 text-lg">
          No per-rep or team targets configured for {label}.
        </div>
      ) : (
        <div className="flex-1 space-y-4 overflow-auto pr-1" data-testid="wall-sales-targets">
          {business && (
            <div>
              <SectionLabel count={1}>Business</SectionLabel>
              <Card row={business} tone="business" />
            </div>
          )}
          {teams && teams.length > 0 && (
            <div>
              <SectionLabel count={teams.length}>Teams</SectionLabel>
              <div className={`grid gap-3 ${teamGrid}`}>
                {teams.map((t) => <Card key={`team-${t.owner}`} row={t} tone="team" compact={teamDensity} />)}
              </div>
            </div>
          )}
          {rows && rows.length > 0 && (
            <div>
              <SectionLabel count={rows.length}>Individual</SectionLabel>
              <div className={`grid gap-3 ${indGrid}`}>
                {rows.map((r, i) => <Card key={`ind-${i}-${r.owner}`} row={r} tone="individual" compact={indDensity} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function LeadsView({ leads, summary }) {
  const stats = [
    { label: "Converted this Q", value: leads.converted_in_period, color: "text-emerald-400" },
    { label: "SQLs this Q", value: leads.sql_in_period, color: "text-blue-400" },
    { label: "MQL (active)", value: leads.mql_now, color: "text-violet-400" },
    { label: "HubSpot queue", value: leads.hubspot_queue, color: "text-orange-400" },
    { label: "🔥 Hot in queue", value: leads.hot_queue, color: "text-rose-400" },
    { label: "Stale (>90d)", value: leads.stale_leads, color: "text-amber-400" },
  ];
  const barFor = (p) =>
    p >= 100 ? "bg-emerald-400" : p >= 60 ? "bg-blue-400" : p >= 30 ? "bg-amber-400" : "bg-rose-400";
  const textFor = (p) => (p >= 100 ? "text-emerald-400" : "text-slate-200");
  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={Users} label="Growth" title="Leads pipeline" />
      {/* Headline Leads actual-vs-target — rendered even at 0% so the goal
          is always on screen. */}
      {summary && (() => {
        const pct = summary.pct || 0;
        const bar = Math.min(100, Math.max(0, pct));
        return (
          <div
            className="mb-4 rounded-2xl border border-emerald-800/60 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 p-5"
            data-testid="wall-leads-summary"
          >
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">
                Converted leads vs target {summary.has_target ? "" : "· no target set yet"}
              </div>
              <div className={`text-5xl font-black tabular-nums ${textFor(pct)}`}>{pct}%</div>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden my-3">
              <div className={`h-full ${barFor(pct)} rounded-full transition-all`} style={{ width: `${bar}%` }} />
            </div>
            <div className="text-sm font-mono text-slate-300 flex items-baseline justify-between">
              <span className="text-white"><span className="text-slate-500">Actual</span> {summary.actual}</span>
              <span className="text-slate-400"><span className="text-slate-500">Target</span> {summary.has_target ? summary.target : "—"}</span>
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-3 gap-6 flex-1 content-start">
        {stats.map((s) => (
          <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-3xl p-8">
            <div className="text-sm font-mono uppercase tracking-wider text-slate-500">{s.label}</div>
            <div className={`mt-3 text-[7rem] leading-none font-black tabular-nums ${s.color}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function TopDeals({ deals }) {
  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={TrendingUp} label="Pipeline" title="Top 10 open deals — closing this quarter" />
      {(!deals || deals.length === 0) ? (
        <div className="flex-1 flex items-center justify-center text-slate-500 text-xl">
          No open deals closing this quarter.
        </div>
      ) : (
        <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <table className="w-full text-xl">
            <thead className="bg-slate-800/50 text-[11px] font-mono uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-6 py-3">Customer / Deal</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-right px-4 py-3">MRR</th>
                <th className="text-right px-4 py-3">Prob</th>
                <th className="text-left px-4 py-3">Owner</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-slate-800">
                  <td className="px-6 py-3">
                    <div className="font-semibold text-white">{d.account_name}</div>
                    <div className="text-sm text-slate-400 truncate">{d.name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-base">{d.stage}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-emerald-400">{gbp(d.revenue)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-slate-300">{gbp(d.mrr)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-slate-400">{d.probability}%</td>
                  <td className="px-4 py-3 text-slate-300 text-base">{d.owner_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ProjectsView({ projects }) {
  const RAG_COLOR = { green: "bg-emerald-400", amber: "bg-amber-400", red: "bg-rose-400", neutral: "bg-slate-500" };
  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={HardHat} label="Delivery" title="Active projects & installs" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Active" value={projects.active_total} color="text-blue-400" />
        <StatCard label="On track" value={projects.on_track} color="text-emerald-400" />
        <StatCard label="At risk" value={projects.at_risk} color="text-amber-400" />
      </div>
      <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-lg">
          <thead className="bg-slate-800/50 text-[11px] font-mono uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-5 py-3">Customer / Project</th>
              <th className="text-left px-4 py-3">Progress</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">CSM</th>
            </tr>
          </thead>
          <tbody>
            {(projects.list || []).map((p) => (
              <tr key={p.id} className="border-t border-slate-800">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-3 h-3 rounded-full ${RAG_COLOR[p.rag] || RAG_COLOR.neutral}`} />
                    <div>
                      <div className="font-semibold text-white text-lg">{p.account_name}</div>
                      <div className="text-sm text-slate-400 truncate">{p.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 w-64">
                  <div className="flex items-center gap-3">
                    <div className="h-2 flex-1 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${p.rag === "amber" ? "bg-amber-400" : "bg-blue-400"}`} style={{ width: `${p.progress_pct}%` }} />
                    </div>
                    <span className="font-mono tabular-nums text-sm text-slate-300 w-14 text-right">
                      {p.steps_done}/{p.steps_total}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-300 text-base">{p.owner_name}</td>
                <td className="px-4 py-3 text-slate-300 text-base">{p.csm_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SupportView({ support }) {
  const stats = [
    { label: "Open tickets", value: support.open_total, color: "text-white" },
    { label: "SLA breaching", value: support.breaching, color: "text-rose-400" },
    { label: "Unassigned", value: support.unassigned, color: "text-amber-400" },
    { label: "Resolved this week", value: support.resolved_this_week, color: "text-emerald-400" },
  ];
  const priorityOrder = ["Urgent", "High", "Medium", "Low"];
  const priorityColor = { Urgent: "text-rose-400", High: "text-orange-400", Medium: "text-blue-400", Low: "text-slate-400" };
  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={LifeBuoy} label="Service desk" title="Support overview" />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {stats.map((s) => <StatCard key={s.label} label={s.label} value={s.value} color={s.color} />)}
      </div>
      <div className="grid grid-cols-2 gap-6 flex-1">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8">
          <div className="text-sm font-mono uppercase tracking-wider text-slate-500 mb-4">Open by priority</div>
          <div className="space-y-4">
            {priorityOrder.map((p) => (
              <div key={p} className="flex items-baseline justify-between">
                <span className={`text-2xl font-bold ${priorityColor[p]}`}>{p}</span>
                <span className={`text-5xl font-black tabular-nums ${priorityColor[p]}`}>{support.by_priority?.[p] || 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8">
          <div className="text-sm font-mono uppercase tracking-wider text-slate-500 mb-4">By category</div>
          <div className="space-y-8 mt-6">
            <div>
              <div className="text-xl text-slate-400">Support</div>
              <div className="text-7xl font-black text-blue-400 tabular-nums">{support.support_open}</div>
            </div>
            <div>
              <div className="text-xl text-slate-400">Service (managed)</div>
              <div className="text-7xl font-black text-violet-400 tabular-nums">{support.service_open}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function StatCard({ label, value, color }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-6xl font-black tabular-nums ${color || "text-white"}`}>{value}</div>
    </div>
  );
}


// ----- Marketing calendar (current fiscal quarter) -----
function MarketingView({ activities, periodLabel }) {
  const acts = activities || [];
  const label = periodLabel || "this quarter";

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); }
    catch { return iso; }
  };
  const timeLabel = (a) => {
    if (a.all_day || !a.start_time) return "";
    return a.end_time ? `${a.start_time}–${a.end_time}` : a.start_time;
  };

  // Group by month for a tidy quarter overview
  const byMonth = acts.reduce((acc, a) => {
    const k = (a.start_date || "").slice(0, 7); // YYYY-MM
    (acc[k] = acc[k] || []).push(a);
    return acc;
  }, {});
  const monthKeys = Object.keys(byMonth).sort();

  return (
    <div className="flex-1 flex flex-col">
      <SlideHeader icon={Megaphone} label="Marketing" title={`Activities — ${label}`} />
      {acts.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-500 text-xl">
          No marketing activities scheduled for {label}.
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
          {monthKeys.map((mk) => {
            const monthLabel = new Date(mk + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" });
            const items = byMonth[mk];
            return (
              <div key={mk} className="bg-slate-900 border border-slate-800 rounded-2xl p-4 overflow-hidden flex flex-col">
                <div className="text-xs font-mono uppercase tracking-wider text-blue-400 mb-3 flex items-baseline gap-2">
                  <span>{monthLabel}</span>
                  <span className="text-slate-600">·  {items.length}</span>
                </div>
                <ul className="space-y-2 overflow-hidden flex-1" data-testid={`wall-marketing-month-${mk}`}>
                  {items.slice(0, 8).map((a) => {
                    const meta = MARKETING_TYPE_META[a.activity_type] || MARKETING_TYPE_META.event;
                    const Icon = meta.Icon;
                    const t = timeLabel(a);
                    return (
                      <li
                        key={a.id}
                        className={`rounded-lg border ${meta.color} px-3 py-2`}
                        data-testid={`wall-marketing-activity-${a.id}`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{meta.label}</span>
                          <span className="ml-auto text-[10px] font-mono tabular-nums opacity-80">
                            {fmtDate(a.start_date)}{a.end_date && a.end_date !== a.start_date ? `→${fmtDate(a.end_date)}` : ""}
                            {t && <span className="ml-1">· {t}</span>}
                          </span>
                        </div>
                        <div className="text-white font-semibold text-sm truncate" title={a.title}>{a.title}</div>
                        {(a.location || a.owner_name) && (
                          <div className="text-[10px] text-slate-400 truncate mt-0.5">
                            {a.location || ""}{a.location && a.owner_name ? " · " : ""}{a.owner_name || ""}
                          </div>
                        )}
                      </li>
                    );
                  })}
                  {items.length > 8 && (
                    <li className="text-[10px] text-slate-500 text-center">+{items.length - 8} more</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
